import {defaultPropertyDefinitions} from "@buildwithflux/constants";
import {
    getProperty,
    DEFAULT_GROUND_TERMINAL_PART_UID,
    OFFICIAL_NET_PORTAL_PART_UID,
    enrichActionData,
    LineSegment,
    seg2,
    box2,
    vec2,
    vec3,
    geom2,
    mat3,
    mat4,
} from "@buildwithflux/core";
import {CopilotActionKey, FluxClaims, IBox2, ICopilotActions, IVector2, NetType} from "@buildwithflux/models";
import {isTestEnv} from "@buildwithflux/shared";
import set from "lodash/set";
import {useEffect} from "react";
import {CanvasTexture, CircleGeometry, Color, Mesh, MeshBasicMaterial, PlaneGeometry, Scene} from "three";
import {Line2, LineGeometry, LineMaterial} from "three-stdlib";

import {useFluxServices} from "injection/hooks";
import {getActiveServicesContainerBadlyAsServiceLocator} from "injection/singleton";
import {createCopilotActions} from "modules/document/state/actions";
import type {DocumentStore} from "modules/document/state/document";
import {usePcbEditorUiStore} from "modules/stores/pcb/PcbEditorUiStore";

/** Copied and modified from autopilot/helpers:schemaToCopilotActions, because that function
 * is not importable here (being in functions-copilot). We should fix that, but I needed a
 * tool quickly.
 * FRAGILE! DO NOT USE! DEFINITELY NEVER EXPORT!
 */
function BAD_UNDRY_COPY_OF_schemaToCopilotActions(response: any): ICopilotActions {
    const actions: ICopilotActions = {};

    // Enumerate each action type below because TypeScript doesn't catch a number of structural type
    // mismatches if we don't.
    if (response.rewriteProjectDescription) {
        actions.rewriteProjectDescription = response.rewriteProjectDescription.projectTitle;
    } else if (response.rewriteProjectProperties) {
        // Ugh. Feels like we should make the types match better than this
        actions.rewriteProjectProperties = {
            projectPropertiesToAddOrEdit: response.rewriteProjectProperties.projectPropertiesToAddOrEdit ?? [],
            projectPropertiesToRemove: response.rewriteProjectProperties.projectPropertiesToRemove ?? [],
        };
    } else if (response.rewriteDesignators) {
        actions.rewriteDesignators = {
            componentDesignatorsToEdit: response.rewriteDesignators.componentDesignatorsToEdit ?? [],
            netDesignatorsToEdit: response.rewriteDesignators.netDesignatorsToEdit ?? [],
        };
    } else if (response.rewriteComponents) {
        actions.rewriteComponents = {
            ...response,
            componentsToAdd: (response.rewriteComponents.componentsToAdd ?? []).map((c: any) => ({
                ...c,
                // TODO in the copilot rewriteComponents.ts code, it also handles the `partName`
                // field that the schema allows the llm to return (as a fallback if it can't
                // find the partUid). We should handle this! Probably by passing partName through
                // so it gets to `applyActions.ts:enrichActionData` where we handle the
                // part lookups. Then we can prob eliminate the code duplication of the copilot
                // version in rewriteComponents.ts:
                componentDesignator: c.designator ?? undefined,
                componentPropertiesToAddOrEdit: c.componentPropertiesToAddOrEdit ?? [],
                quantity: c.quantity ?? undefined,
                group: c.group ?? undefined,
                // TODO painful we have to typecast below because zod.nullable allows `null`
                // as well as `undefined` in these fields, but our type only allows undefined.
                // And openai/langchain requires we use zod.nullable for optional fields!
            })),
            componentsToRemove: response.rewriteComponents.componentsToRemove ?? [],
        };
    } else if (response.rewriteComponentProperties) {
        actions.rewriteComponentProperties = response.rewriteComponentProperties.components.map((c: any) => ({
            ...c,
            componentPropertiesToAddOrEdit: c.componentPropertiesToAddOrEdit ?? [],
            componentPropertiesToRemove: c.componentPropertiesToRemove ?? [],
        }));
    } else if (response.rewriteProjectNets) {
        actions.rewriteProjectNets = response.rewriteProjectNets.nets;
    } else if (response.rewriteLayoutRules) {
        actions.rewriteLayoutRules = response.rewriteLayoutRules.pcbNodes.map((n: any) => ({
            ...n,
            rulesToAddOrEdit: (n.rulesToAddOrEdit ?? []).map((r: any) => ({
                ...r,
                order: r.order ?? undefined,
                disabled: r.disabled ?? undefined,
            })),
            rulesToRemove: n.rulesToRemove ?? [],
        }));
    }

    return actions;
}

/** Allow adding components using a direct copy-paste of the JSON action data
 * returned by the rewrite_components tool, to be found eg in a langsmith trace.
 */
async function applyActionData(actionName: CopilotActionKey, data: any) {
    const services = getActiveServicesContainerBadlyAsServiceLocator();
    if (typeof data === "string") {
        data = JSON.parse(data);
    }
    const actions = BAD_UNDRY_COPY_OF_schemaToCopilotActions({[actionName]: data});
    const {actions: enrichedActions, actionContext} = await enrichActionData(actions, services);

    // eslint-disable-next-line no-console
    console.log("Applying action data:", enrichedActions);
    let applyResult;
    const copilotActions = createCopilotActions(services.documentStore);
    copilotActions.applyCopilotActions({
        actions: enrichedActions,
        ...actionContext,
        userUid: services.currentUserService.getCurrentUser()?.uid ?? "",
        resultCallback: (result) => (applyResult = result),
    });

    // eslint-disable-next-line no-console
    console.log("Applied action data", applyResult);
    return applyResult;
}

async function connectNetsByDescriptor(netName: string, pins: string[]) {
    const services = getActiveServicesContainerBadlyAsServiceLocator();
    const portalPart = await services.partLibrary.get({partUid: OFFICIAL_NET_PORTAL_PART_UID, partVersion: "latest"});
    const groundPart = await services.partLibrary.get({
        partUid: DEFAULT_GROUND_TERMINAL_PART_UID,
        partVersion: "latest",
    });
    if (!portalPart || !groundPart) {
        return;
    }
    services.documentStore.getState().connectNetsByDescriptor(
        [
            {
                netName,
                pins: pins.map((p) => ({component: p.split(":")[0] ?? "", pinDesignator: p.split(":")[1] ?? ""})),
            },
        ],
        portalPart,
        groundPart,
        "pEB9EbyvhzTfP8VjqzydtXt0tPy1",
    );
}

/**
 * Creates a test case object for the classifyNets eval suite based on the current document
 */
function createClassifyNetsTestCase(documentStore: DocumentStore) {
    return () => {
        // Get document from Zustand store instead of Redux state
        const document = documentStore.getState().document;
        if (!document) {
            throw new Error("No document currently loaded");
        }

        const netClassifications: Record<string, NetType> = {};

        for (const net of Object.values(document.nets)) {
            const netTypeProperty = getProperty(defaultPropertyDefinitions.net_type.label, net.properties);

            if (netTypeProperty && netTypeProperty.value) {
                const key = net.label || net.uid;
                netClassifications[key] = netTypeProperty.value as NetType;
            }
        }

        const projectUrl = window.location.href;
        const documentUid = document.uid;

        return {
            name: document.name,
            documentUid,
            description: `Test classification of nets from ${document.name}`,
            projectUrl,
            numRuns: 1,
            assertion: netClassifications,
        };
    };
}

function getCurrentUser() {
    return (window as any).__FLUX__.services.currentUserService.getCurrentUser();
}

function getDocument() {
    return (window as any).__FLUX__.services.documentStore.getState().document;
}

function getScene(): Scene | undefined {
    const editorMode = (window as any).__FLUX__.getEditorMode();
    if (editorMode === "schematic") {
        return (window as any).__FLUX__.sceneManager?.scene;
    } else if (["pcb"].includes(editorMode)) {
        return (window as any).__FLUX__.usePcbEditorUiStore?.getState()?.pcbEditorR3FStore?.scene;
    }
    return undefined;
}

function getCanvas(): HTMLCanvasElement | undefined {
    const editorMode = (window as any).__FLUX__.getEditorMode();
    if (editorMode === "schematic") {
        return (window as any).__FLUX__.sceneManager?.canvas;
    } else if (["pcb"].includes(editorMode)) {
        return (window as any).__FLUX__.usePcbEditorUiStore?.getState()?.pcbEditorR3FStore?.gl.domElement;
    }
    return undefined;
}

type DebugPlotOpts = {
    // thickness of line or diameter (not radius) of point
    width?: number;
    color?: string;
    fontSize?: number;
    label?: string;
    z?: number;
};

let debugPlots: Mesh[] = [];

function clearPlots() {
    const scene = getScene();
    for (const plot of debugPlots) {
        scene?.remove(plot);
    }
    debugPlots = [];
}

function plotPoint(point: IVector2, opts: DebugPlotOpts = {}) {
    const scene = getScene();
    if (!scene) return;
    const editorMode = (window as any).__FLUX__.getEditorMode();
    const size = opts.width ?? (editorMode === "schematic" ? 2 : 0.001);
    const z = opts.z ?? (editorMode === "schematic" ? 0 : 0.5);
    const geometry = new CircleGeometry(size / 2, 32);
    const material = new MeshBasicMaterial({color: opts.color ?? "white"});
    const circle = new Mesh(geometry, material);
    circle.position.set(point.x, point.y, z);
    scene.add(circle);
    debugPlots.push(circle);
}

function plotPoints(points: IVector2[], opts: DebugPlotOpts = {}) {
    for (const point of points) {
        plotPoint(point, opts);
    }
}

function plotLine(segment: LineSegment, opts: DebugPlotOpts = {}) {
    const scene = getScene();
    const canvas = getCanvas();
    if (!scene || !canvas) return;
    const editorMode = (window as any).__FLUX__.getEditorMode();
    const z = opts.z ?? (editorMode === "schematic" ? 0 : 0.5);
    const positions = [segment[0].x, segment[0].y, z, segment[1].x, segment[1].y, z];
    const geometry = new LineGeometry();
    geometry.setPositions(positions);
    const material = new LineMaterial({
        color: new Color(opts.color ?? "white").getHex(),
        linewidth: opts.width ?? 1,
    });
    material.resolution.set(canvas.width, canvas.height);
    const line = new Line2(geometry, material);
    scene.add(line);
    debugPlots.push(line);
}

function plotPoly(poly: IVector2[], opts: DebugPlotOpts = {}) {
    const segments = poly.length < 2 ? [] : poly.map((p, i, arr) => [p, arr[(i + 1) % arr.length]]);
    for (const segment of segments) {
        plotLine(segment as LineSegment, opts);
    }
}

function plotBox(box: IBox2, opts: DebugPlotOpts = {}) {
    plotPoly([box.min, {x: box.min.x, y: box.max.y}, box.max, {x: box.max.x, y: box.min.y}], opts);
    if (opts.label) {
        const fontSize = opts.fontSize ?? 8;
        opts = {...opts, fontSize};
        plotText(opts.label!, {x: box.min.x, y: box.max.y + fontSize}, opts);
    }
}

function plotText(text: string, pos: IVector2, opts: DebugPlotOpts = {}) {
    const scene = getScene();
    if (!scene) return;

    const finalHeight = opts.fontSize ?? 10;

    // Create canvas texture
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;

    // Render at highish res for good smoothness, then scale the mesh to size later
    const renderScale = 50;

    ctx.font = `${renderScale}px sans-serif`;
    const metrics = ctx.measureText(text);
    canvas.width = metrics.width;
    canvas.height = renderScale;

    // Canvas context gets reset after updating canvas dimensions, so need to reassign font size etc.
    ctx.font = `${renderScale}px sans-serif`;
    ctx.fillStyle = opts.color ?? "white";
    ctx.textAlign = "center";
    ctx.fillText(text, canvas.width / 2, canvas.height * 0.8); // Fudge factor to allow descenders. Hack, but it's for debug only

    const texture = new CanvasTexture(canvas);
    const material = new MeshBasicMaterial({map: texture, transparent: true});
    const geometry = new PlaneGeometry((metrics.width * finalHeight) / renderScale, finalHeight);
    const mesh = new Mesh(geometry, material);

    const editorMode = (window as any).__FLUX__.getEditorMode();
    const z = opts.z ?? (editorMode === "schematic" ? 0 : 0.5);
    mesh.position.set(pos.x, pos.y, z);

    scene.add(mesh);
    debugPlots.push(mesh);
}

function exposeGeomUtils() {
    (window as any).seg2 = seg2;
    (window as any).box2 = box2;
    (window as any).vec2 = vec2;
    (window as any).vec3 = vec3;
    (window as any).geom2 = geom2;
    (window as any).mat3 = mat3;
    (window as any).mat4 = mat4;
}

export function hasFluxConsoleAccess(claims: FluxClaims) {
    return claims?.admin || claims?.superadmin || claims?.internal;
}

/**
 * Hook to add Flux helpers to the browser console for admin users.
 */
export function useFluxConsoleHelpers() {
    const {useFluxClaimsStore, useFeatureFlagsStore, documentStore, usePersistedDocumentUiStore} = useFluxServices();
    const flags = useFeatureFlagsStore();
    const hasConsoleAccess = useFluxClaimsStore(({claims}) => hasFluxConsoleAccess(claims));

    useEffect(() => {
        if (isTestEnv()) {
            return;
        }

        if (!hasConsoleAccess) {
            return;
        }

        set(window, ["__FLUX__", "usePcbEditorUiStore"], usePcbEditorUiStore);
        set(window, ["__FLUX__", "featureFlags"], flags);
        set(window, ["__FLUX__", "eval", "createClassifyNetsTestCase"], createClassifyNetsTestCase(documentStore));
        set(window, ["__FLUX__", "connectNetsByDescriptor"], connectNetsByDescriptor);
        set(window, ["__FLUX__", "applyActionData"], applyActionData);
        set(window, ["__FLUX__", "getDocument"], getDocument);
        set(window, ["__FLUX__", "getCurrentUser"], getCurrentUser);
        set(window, ["__FLUX__", "getScene"], getScene);
        set(window, ["__FLUX__", "getEditorMode"], () => usePersistedDocumentUiStore.getState().editorMode);
        set(window, ["__FLUX__", "plotPoint"], plotPoint);
        set(window, ["__FLUX__", "plotPoints"], plotPoints);
        set(window, ["__FLUX__", "plotLine"], plotLine);
        set(window, ["__FLUX__", "plotPoly"], plotPoly);
        set(window, ["__FLUX__", "plotBox"], plotBox);
        set(window, ["__FLUX__", "plotText"], plotText);
        set(window, ["__FLUX__", "clearPlots"], clearPlots);
        set(window, ["__FLUX__", "exposeGeomUtils"], exposeGeomUtils);
    }, [flags, hasConsoleAccess, documentStore, usePersistedDocumentUiStore]);
}
